//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useState, useContext, useEffect } from 'react';
import { Avatar, makeStyles } from '@material-ui/core';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { unsubscribeRealtimeUpdates } from '../../system/firebase';
import { LayoutContext, AppContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { downgradeTierSubscription } from '../../common/services/empMySettingsServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdEditDialog from '../../common/elemental/AfitdEditDialog';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
// import SubComponent1Example from './SubComponent1Example';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
import _get from 'lodash/get';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    return makeStyles(theme => ({
        heading: { fontWeight: 'bold' },
        sectionTitle: { fontWeight: 'bold', marginTop: 20 },

        listContainer: { display: 'flex', alignItems: 'center', padding: '15px 0', borderBottom: '2px solid rgb(240,242,246)' },
        displayPicture: { color: 'rgb(252,130,31)', background: 'rgba(252, 130, 31, 0.1)', marginRight: 20, height: 50, width: 50 },
        name: { fontWeight: 'bold' },
        email: { fontWeight: 'bold' },
        grayedText: { color: 'rgb(155,158,156)' },
        invitationSentText: { color: 'rgb(252,130,31)' },

        inline: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 },
        userInfo: { flexGrow: 1 },
        removeText: { color: 'red', fontWeight: 'bold', cursor: 'pointer', marginRight: 10 },
        hidden: { display: 'none' },
        seatsToRemove: { background: 'rgba(29,138,139,0.09)', padding: 10 },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const { classes, props, handleClickSaveButton, membersNew, owner } = params;
    const { open, onClose, tiers, selectedTier } = props;
    const { hidden: hiddenCls, seatsToRemove: seatsToRemoveCls } = classes;

    const currentSeats = membersNew.length + 1;
    const allowedSeats = tiers?.find(el => el.tier === selectedTier)?.seatMax;

    const shouldRemoveSeats = currentSeats > allowedSeats;
    const noOfSeatsToRemove = shouldRemoveSeats ? currentSeats - allowedSeats : 0;

    const isDirty = noOfSeatsToRemove <= 0;

    const defns = {
        container: {
            open,
            onClose,
            label: `Subscription and Billing - Downgrade | Seat Reduction (Tier ${selectedTier})`,
            actionButtonDefns: {
                save: {
                    disabled: !isDirty,
                    onClick: () => handleClickSaveButton([owner, ...membersNew]),
                    label: `Downgrade to Tier ${selectedTier}`,
                },
            },
        },
        sectionTitle: { variant: 'body2' },

        name: { variant: 'body1' },
        grayedText: { variant: 'body1', component: 'span' },
        email: { variant: 'body2' },

        captionText: { variant: 'caption' },
        invitationSentText: { variant: 'body2', value: 'Invitation Sent' },
        removeText: { variant: 'body1', value: 'Remove' },

        seatsToRemove: {
            variant: 'body2',
            value: `You have to remove ${noOfSeatsToRemove} ${noOfSeatsToRemove === 1 ? ' seat' : ' seats'} for this plan`,
            className: shouldRemoveSeats ? seatsToRemoveCls : hiddenCls,
        },
    };

    setClassNameInDefns(defns, classes);
    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const DowngradeDialog = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles(/*{param1, param2}*/)();

    const appContext = useContext(AppContext);
    const { currentEmployer, employerId } = appContext;

    const layoutContext = useContext(LayoutContext);
    const { setSnackbarMessage, setSnackbarErrorMessage } = layoutContext;

    const members = _get(currentEmployer, 'members', []);
    const owner = members.find(el => el.isOwner);
    const otherMembers = members.filter(el => !el.isOwner);

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { onClose, setIsDirtyFlagNull, selectedTier, hideSeatReductionModal } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [membersNew, setMembersNew] = useState([]);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const existingTier = currentEmployer?.tier;
    const existingMonthly = currentEmployer?.billMonthly;
    const stripeSubId = currentEmployer?.stripeSubId;
    const stripeAdditionalSeatSubId = currentEmployer?.stripeAdditionalSeatSubId;

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    //TODO Rework this useEffect -el 20220615
    useEffect(() => {
        if (selectedTier === 2 && members.length <= 3 && hideSeatReductionModal) {
            onClose && onClose();
            handleClickSaveButton(members);
        } else if (selectedTier === 1 && hideSeatReductionModal) {
            onClose && onClose();
            handleClickSaveButton([owner]);
        } else setMembersNew(otherMembers);

        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hideSeatReductionModal]);

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleClickSaveButton = async value => {
        downgradeTierSubscription({
            membersNew: value,
            tierNew: selectedTier,
            // billMonthlyNew, // comment this out so it will use existingMonthly
            employerId,
            stripeSubId,
            stripeAdditionalSeatSubId,
            existingTier,
            existingMonthly,
            setSnackbarMessage,
            setSnackbarErrorMessage,
        });
        onClose && onClose();
        setIsDirtyFlagNull();
    };

    const removeMember = val => {
        setMembersNew(otherMembers.filter(el => el.userId !== val.userId));
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({ classes, props, handleClickSaveButton, membersNew, owner });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <AfitdEditDialog {...defns.container}>
            <AfitdTypography {...defns.sectionTitle}>Owner</AfitdTypography>
            <div {...defns.listContainer}>
                <Avatar {...defns.displayPicture} src={owner?.profilePicUrl}>
                    ?
                </Avatar>
                <div>
                    <AfitdTypography {...defns.name}>
                        {owner?.firstName} {owner?.lastName}
                        <AfitdTypography {...defns.grayedText}> (You)</AfitdTypography>
                    </AfitdTypography>
                    <AfitdTypography {...defns.email}>{owner?.email}</AfitdTypography>
                </div>
            </div>
            <AfitdTypography {...defns.sectionTitle}>Team Members</AfitdTypography>
            <div {...defns.inline}>
                <AfitdTypography {...defns.captionText}>Name</AfitdTypography>
                <AfitdTypography {...defns.captionText}>Role(s)</AfitdTypography>
            </div>
            {membersNew?.map(el => (
                <div {...defns.listContainer}>
                    <Avatar {...defns.displayPicture} src={el?.profilePicUrl}>
                        ?
                    </Avatar>
                    <div {...defns.userInfo}>
                        <AfitdTypography {...defns.name}>
                            {el?.firstName} {el?.lastName}
                        </AfitdTypography>
                        <AfitdTypography {...defns.email}>{el?.email}</AfitdTypography>
                    </div>
                    <AfitdTypography {...defns.removeText} onClick={() => removeMember(el)} />
                </div>
            ))}
            <AfitdTypography {...defns.seatsToRemove} />
        </AfitdEditDialog>
    );
};

export default DowngradeDialog;
